import {
  adverseMediaTypeDisplayText,
  AdverseMediaTypeV2,
} from 'dashboard/models/report/adverse-media';

export enum AdverseMediaCategoryType {
  FATF = 'FATF',
  EUAML = 'EU AML',
  AMLCFT = 'AML/CFT',
}

export const ADVERSE_MEDIA_CATEGORY_TYPE_DISPLAY_MAP = {
  [AdverseMediaCategoryType.FATF]: 'Financial Action Task Force',
  [AdverseMediaCategoryType.EUAML]: 'EU Anti-Money Laundering Directive',
  [AdverseMediaCategoryType.AMLCFT]: 'Anti-Money Laundering/Combating the Financing of Terrorism',
};

export const ADVERSE_MEDIA_CATEGORY_OPTIONS = [
  {
    categories: [AdverseMediaCategoryType.FATF, AdverseMediaCategoryType.EUAML],
    name: adverseMediaTypeDisplayText(AdverseMediaTypeV2.V2Property),
    value: AdverseMediaTypeV2.V2Property,
    info:
      'Topics involving the unlawful taking of property, including money and intellectual property (piracy / copyright infringement). Includes theft, ' +
      'burglary, and possession of stolen property. Also includes embezzlement, misappropriation, larceny.',
  },
  {
    categories: [
      AdverseMediaCategoryType.AMLCFT,
      AdverseMediaCategoryType.FATF,
      AdverseMediaCategoryType.EUAML,
    ],
    name: adverseMediaTypeDisplayText(AdverseMediaTypeV2.V2FinancialAMLCFT),
    value: AdverseMediaTypeV2.V2FinancialAMLCFT,
    info: 'Financial topics related to AML/CFT, that are not explicitly property crime. Includes money laundering, tax related offences, and insider trading.',
  },
  {
    categories: [AdverseMediaCategoryType.FATF, AdverseMediaCategoryType.EUAML],
    name: adverseMediaTypeDisplayText(AdverseMediaTypeV2.V2FraudLinked),
    value: AdverseMediaTypeV2.V2FraudLinked,
    info:
      'Fraud is intentional deception to secure unfair or unlawful gain, or to deprive a victim of a legal right. Specific topics include ' +
      'fraud (corporate, VAT, identity, etc.), scams, counterfeiting, and forgery.',
  },
  {
    categories: [
      AdverseMediaCategoryType.AMLCFT,
      AdverseMediaCategoryType.FATF,
      AdverseMediaCategoryType.EUAML,
    ],
    name: adverseMediaTypeDisplayText(AdverseMediaTypeV2.V2NarcoticsAMLCFT),
    value: AdverseMediaTypeV2.V2NarcoticsAMLCFT,
    info: 'All producing, trafficking, distribution, and dealing of narcotics (drugs) - will be linked to AML as a predicate offence. Does not include drug usage or possession.',
  },
  {
    categories: [
      AdverseMediaCategoryType.AMLCFT,
      AdverseMediaCategoryType.FATF,
      AdverseMediaCategoryType.EUAML,
    ],
    name: adverseMediaTypeDisplayText(AdverseMediaTypeV2.V2ViolenceAMLCFT),
    value: AdverseMediaTypeV2.V2ViolenceAMLCFT,
    info:
      'Serious violence related topics that are relevant to financial institutions. Primarily focused on FATF and EU directive ' +
      'predicate offences. Excludes non predicate offences (assault, most sexual crimes) but does include murder and grievous ' +
      'bodily harm. Includes topics where violence is implied and/or there are AML implications: kidnapping; human trafficking; ' +
      'child pornography; illegal arms / weapons possession / trading; pimping; slavery.',
  },
  {
    categories: [AdverseMediaCategoryType.FATF, AdverseMediaCategoryType.EUAML],
    name: adverseMediaTypeDisplayText(AdverseMediaTypeV2.V2Terrorism),
    value: AdverseMediaTypeV2.V2Terrorism,
    info: 'All terrorism related topics including attacks, financing, membership, and aiding. Includes all forms of terrorism - religious, political (left & right wing), state.',
  },
  {
    categories: [AdverseMediaCategoryType.EUAML],
    name: adverseMediaTypeDisplayText(AdverseMediaTypeV2.V2Cybercrime),
    value: AdverseMediaTypeV2.V2Cybercrime,
    info:
      'Related to computer related topics and crime, in particular illegal: Access to information systems; system interference; data ' +
      'interference; interception. Does not include topics only because computers are involved (such as fraud), but only topics ' +
      'where it is specifically computer/cyber related.',
  },
  {
    categories: [
      AdverseMediaCategoryType.AMLCFT,
      AdverseMediaCategoryType.FATF,
      AdverseMediaCategoryType.EUAML,
    ],
    name: adverseMediaTypeDisplayText(AdverseMediaTypeV2.V2GeneralAMLCFT),
    value: AdverseMediaTypeV2.V2GeneralAMLCFT,
    info:
      'Includes major topics related to AML and CFT. Different to the "Other" categories. Topics include: Smuggling (goods & ' +
      'migrants); organised crime and gang activity; corruption and bribery; environmental crimes including poaching and ' +
      'polluting; potential sanctions violations, risks, or proximity to sanctioned entities.',
  },
  {
    categories: [],
    name: adverseMediaTypeDisplayText(AdverseMediaTypeV2.V2Regulatory),
    value: AdverseMediaTypeV2.V2Regulatory,
    info:
      'Topics that relate to activity that contravenes regulation - often this is specific to a particular country or state or ' +
      'industry. Includes: Fines or disciplinary action by regulators and industry bodies; collusion / anti-trust / price-fixing ' +
      'issues; specific entities that have been denied by industry bodies and regulators; specific industry related issues such ' +
      'as front-running, greenmail, and churning for the stock market, or illegal gambling.',
  },
  {
    categories: [],
    name: adverseMediaTypeDisplayText(AdverseMediaTypeV2.V2FinancialDifficulty),
    value: AdverseMediaTypeV2.V2FinancialDifficulty,
    info:
      'Topics where the subject is mentioned in the context of bankruptcy, having a high amount of debt, or experiencing fatal ' +
      'business issues. Media may include stories about store closures, lay offs, late payment to creditors, or the unexpected ' +
      'departure of a CFO.',
  },
  {
    categories: [],
    name: adverseMediaTypeDisplayText(AdverseMediaTypeV2.V2ViolenceNonAMLCFT),
    value: AdverseMediaTypeV2.V2ViolenceNonAMLCFT,
    info:
      'Violence related topics that are not predicate offences for AML/CFT - everything violence related that is not specified in ' +
      'the "Violence AML/CFT". Includes assault, domestic violence & physical abuse, harassment, rape & sex offences. Also ' +
      'includes some state level topics such as massacres, human rights abuses, torture, and genocide.',
  },
  {
    categories: [],
    name: adverseMediaTypeDisplayText(AdverseMediaTypeV2.V2OtherFinancial),
    value: AdverseMediaTypeV2.V2OtherFinancial,
    info: 'Non AML/CFT related financial topics such as predatory lending, questionable billing practices, creative accounting, loan sharking, or tax avoidance.',
  },
  {
    categories: [],
    name: adverseMediaTypeDisplayText(AdverseMediaTypeV2.V2OtherSerious),
    value: AdverseMediaTypeV2.V2OtherSerious,
    info:
      'Potentially serious topics that are not AML/CFT related. Many different topics included such as perjury, arson, ' +
      'obstruction of justice, election interference, escaped fugitives, spying & espionage, treason, and civil disorder. Also ' +
      'includes non-specific crimes where very serious penalties are involved (e.g. death / life sentences).',
  },
  {
    categories: [],
    name: adverseMediaTypeDisplayText(AdverseMediaTypeV2.V2OtherMinor),
    value: AdverseMediaTypeV2.V2OtherMinor,
    info:
      'Other minor topics and difficult to classify topics that do not fall into any of the other classes. Includes things like drugs ' +
      'posession, prostitution, misconduct, conspiracy to commit a crime where the crime is not specified, vandalism, drink ' +
      'driving / DUI (driving under the influence), and commercial litigation between entities. May also include some more ' +
      'general corporate related topics like human resources issues / labour disputes, and health & safety.',
  },
];

// This maps to AwsComprehendClient::SUPPORTED_LANGUAGES
export const ADVERSE_MEDIA_SUPPORTED_LANGUAGES = {
  af: 'Afrikaans',
  am: 'Amharic',
  ar: 'Arabic',
  as: 'Assamese',
  az: 'Azerbaijani',
  ba: 'Bashkir',
  be: 'Belarusian',
  bn: 'Bengali',
  bs: 'Bosnian',
  bg: 'Bulgarian',
  ca: 'Catalan',
  ceb: 'Cebuano',
  cs: 'Czech',
  cv: 'Chuvash',
  cy: 'Welsh',
  da: 'Danish',
  de: 'German',
  el: 'Greek',
  en: 'English',
  eo: 'Esperanto',
  et: 'Estonian',
  eu: 'Basque',
  fa: 'Persian',
  fi: 'Finnish',
  fr: 'French',
  gd: 'Scottish Gaelic',
  ga: 'Irish',
  gl: 'Galician',
  gu: 'Gujarati',
  ht: 'Haitian',
  he: 'Hebrew',
  ha: 'Hausa',
  hi: 'Hindi',
  hr: 'Croatian',
  hu: 'Hungarian',
  hy: 'Armenian',
  ilo: 'Iloko',
  id: 'Indonesian',
  is: 'Icelandic',
  it: 'Italian',
  jv: 'Javanese',
  ja: 'Japanese',
  kn: 'Kannada',
  ka: 'Georgian',
  kk: 'Kazakh',
  km: 'Central Khmer',
  ky: 'Kirghiz',
  ko: 'Korean',
  ku: 'Kurdish',
  lo: 'Lao',
  la: 'Latin',
  lv: 'Latvian',
  lt: 'Lithuanian',
  lb: 'Luxembourgish',
  ml: 'Malayalam',
  mt: 'Maltese',
  mr: 'Marathi',
  mk: 'Macedonian',
  mg: 'Malagasy',
  mn: 'Mongolian',
  ms: 'Malay',
  my: 'Burmese',
  ne: 'Nepali',
  new: 'Newari',
  nl: 'Dutch',
  no: 'Norwegian',
  or: 'Oriya',
  om: 'Oromo',
  pa: 'Punjabi',
  pl: 'Polish',
  pt: 'Portuguese',
  ps: 'Pushto',
  qu: 'Quechua',
  ro: 'Romanian',
  ru: 'Russian',
  sa: 'Sanskrit',
  si: 'Sinhala',
  sk: 'Slovak',
  sl: 'Slovenian',
  sd: 'Sindhi',
  so: 'Somali',
  es: 'Spanish',
  sq: 'Albanian',
  sr: 'Serbian',
  su: 'Sundanese',
  sw: 'Swahili',
  sv: 'Swedish',
  ta: 'Tamil',
  tt: 'Tatar',
  te: 'Telugu',
  tg: 'Tajik',
  tl: 'Tagalog',
  th: 'Thai',
  tk: 'Turkmen',
  tr: 'Turkish',
  ug: 'Uighur',
  uk: 'Ukrainian',
  ur: 'Urdu',
  uz: 'Uzbek',
  vi: 'Vietnamese',
  yi: 'Yiddish',
  yo: 'Yoruba',
  zh: 'Chinese (Simplified)',
  'zh-TW': 'Chinese (Traditional)',
};

export type AdverseMediaLanguageCode = keyof typeof ADVERSE_MEDIA_SUPPORTED_LANGUAGES;
